import React, { useState } from 'react';
import NavBar from './NavBar';
import { 
  Sun, Menu, X, Clock, Activity, Coffee, Baby, Heart, 
  Smile, Music, Book, Utensils, Moon, Shield, Users, HeartHandshake
} from 'lucide-react';
import nurseJourImage from './nurse-jour-image.jpg'; // Image à ajouter

const NurseJourPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const babyServices = [
    "Soins du quotidien : biberon, allaitement, change, endormissement",
    "Respect du rythme de sommeil et d'éveil",
    "Présence douce et rassurante",
    "Stimulation sensorielle adaptée"
  ];

  const childServices = [
    "Jeux éducatifs, activités calmes ou créatives",
    "Sorties possibles (balade au parc, selon autorisation parentale)",
    "Accompagnement dans les routines (repas, sieste, propreté)",
    "Soutien émotions et écoute bienveillante"
  ];

  const reasons = [
    {
      title: "Relais ponctuel ou régulier",
      description: "Un accompagnement flexible selon vos besoins quotidiens",
      icon: Clock
    },
    {
      title: "Soutien post-partum",
      description: "Une aide précieuse lors du retour de maternité",
      icon: Baby
    },
    {
      title: "Situations particulières",
      description: "Support lors d'hospitalisation ou absence du coparent",
      icon: HeartHandshake
    },
    {
      title: "Accompagnement respectueux",
      description: "Aide adaptée en cas de fatigue ou besoins spécifiques",
      icon: Heart
    }
  ];

  const approach = [
    {
      title: "Respect des valeurs familiales",
      description: "Nous respectons les rythmes et valeurs éducatives de chaque famille",
      icon: Users
    },
    {
      title: "Sécurité et douceur",
      description: "Une approche axée sur la sécurité et la stimulation positive",
      icon: Shield
    },
    {
      title: "Confiance et transparence",
      description: "Une relation basée sur la communication ouverte",
      icon: HeartHandshake
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <NavBar />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-amber-400 to-orange-500 overflow-hidden">
        <div className="absolute inset-0">
          <div className="sunshine"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center px-4 py-16 md:py-24">
          <Sun className="w-20 h-20 text-white mx-auto mb-6 animate-spin-slow" />
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 md:mb-6">
            Nurse de Jour
          </h1>
          <p className="text-xl md:text-2xl text-amber-50 font-medium">
            Accompagnement à domicile pour bébés et enfants
          </p>
          
        </div>
      </div>

      {/* Introduction avec image */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="md:flex">
            <div className="md:w-1/2 p-8">
             <h2 className="text-2xl font-bold text-slate-800 mb-8">☀️ Un soutien bienveillant et professionnel pour vos journées en famille</h2>
             
              <p className="text-slate-600 mb-6 text-lg">
                Vous êtes parent d'un <strong>bébé ou d'un jeune enfant</strong> ? Vous avez besoin d'aide pendant la journée pour souffler, vous reposer ou reprendre une activité ?
              </p>
              <p className="text-slate-600 mb-6 text-lg">
                <strong>Nurse de Jour</strong> vous propose un service de <strong>garde de jour à domicile</strong>, assuré par des professionnelles expérimentées et spécialisées en petite enfance.
              </p>
              <p className="text-amber-600 font-medium text-lg">
                Nous intervenons chez vous en journée, selon vos besoins : ponctuellement, quelques heures, ou de façon régulière.
              </p>
            </div>
            <div className="md:w-1/2">
              <img 
                src={nurseJourImage} 
                alt="Nurse de jour avec enfant" 
                className="w-full h-full object-cover"
                style={{ minHeight: '400px' }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Nos Services */}
      <div className="bg-amber-50 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-4">
            Nos services – Garde de jour à domicile pour bébés et enfants
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-3xl mx-auto">
            Nos prestations s'adaptent à l'âge et aux besoins de votre enfant :
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Pour les bébés */}
            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="flex items-center mb-6">
                <Baby className="w-10 h-10 text-amber-500 mr-4" />
                <h3 className="text-2xl font-bold text-slate-800">Pour les bébés</h3>
              </div>
              <ul className="space-y-3">
                {babyServices.map((service, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-amber-500 mr-3 text-xl">•</span>
                    <span className="text-slate-600">{service}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Pour les jeunes enfants */}
            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="flex items-center mb-6">
                <Smile className="w-10 h-10 text-orange-500 mr-4" />
                <h3 className="text-2xl font-bold text-slate-800">Pour les jeunes enfants</h3>
              </div>
              <ul className="space-y-3">
                {childServices.map((service, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-orange-500 mr-3 text-xl">•</span>
                    <span className="text-slate-600">{service}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="text-center mt-8 bg-white rounded-lg p-6 shadow-md">
            <p className="text-lg text-slate-700">
              Notre objectif : <strong className="text-amber-600">soulager les parents</strong>, tout en <strong className="text-amber-600">apportant un cadre sécurisant et stimulant</strong> à chaque enfant.
            </p>
          </div>
        </div>
      </div>

      {/* Pourquoi faire appel à une nurse de jour */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-4">
          Pourquoi faire appel à une nurse de jour ?
        </h2>
        <p className="text-center text-slate-600 mb-12 max-w-3xl mx-auto">
          Nos services sont pensés pour les familles qui ont besoin :
        </p>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {reasons.map((reason, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition-shadow">
              <reason.icon className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-slate-800 mb-2">{reason.title}</h3>
              <p className="text-slate-600 text-sm">{reason.description}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-8">
          <p className="text-lg text-slate-700 bg-amber-50 rounded-lg p-6 inline-block">
            Nous vous aidons à <strong className="text-amber-600">retrouver un équilibre familial</strong> sans stress ni culpabilité.
          </p>
        </div>
      </div>

      {/* Notre approche */}
      <div className="bg-gradient-to-br from-amber-100 to-orange-100 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-4">
            Une approche bienveillante, professionnelle et sur mesure
          </h2>
          
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <p className="text-lg text-slate-700 text-center mb-8">
              Nos nurses sont des <strong>infirmières, auxiliaires de puériculture ou professionnelles de la petite enfance</strong>, 
              toutes formées aux besoins spécifiques des enfants et à l'accompagnement parental.
            </p>
            
            <h3 className="text-xl font-bold text-slate-800 mb-6 text-center">Elles interviennent dans une démarche :</h3>
            
            <div className="grid md:grid-cols-3 gap-6">
              {approach.map((item, index) => (
                <div key={index} className="text-center">
                  <item.icon className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                  <h4 className="font-bold text-slate-800 mb-2">{item.title}</h4>
                  <p className="text-slate-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center bg-white rounded-lg p-8 shadow-md">
            <p className="text-xl text-slate-700">
              Vous gardez le lien avec votre enfant, tout en <strong className="text-amber-600">rechargeant vos batteries l'esprit serein</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Offrez-vous des journées sereines
          </h2>
          <p className="text-xl mb-8 text-amber-50">
            Nos nurses de jour accompagnent votre enfant avec professionnalisme et bienveillance
          </p>
          <a 
            href="mailto:etoileveilleuse@gmail.com"
            className="inline-block bg-white text-orange-600 px-8 py-4 rounded-full font-semibold hover:bg-amber-50 transform transition hover:scale-105"
          >
            Réserver une Nurse de Jour
          </a>
        </div>
      </div>

      {/* Styles pour l'animation soleil */}
      <style jsx>{`
        .sunshine {
          position: absolute;
          width: 100%;
          height: 100%;
          background: radial-gradient(circle at center, rgba(255,255,255,0.2) 0%, transparent 70%);
          animation: pulse 3s ease-in-out infinite;
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
        
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default NurseJourPage;